// Side panel — правая выезжающая панель (tutor, notifications и т.п.).
// Не персистится: при перезагрузке всегда закрыта.
import { create } from "zustand";

export type SidePanelKind = "tutor" | "notifications" | null;

interface SidePanelState {
	open: boolean;
	kind: SidePanelKind;
	/** Идентификатор для повторного открытия конкретной сессии (chat id и т.п.). */
	panelKey: string | null;
	/**
	 * Готовый вопрос для предзаполнения композера репетитора при открытии панели
	 * (кнопки «Спросить репетитора» / «Объясни проще» со страниц visual/audio).
	 * Панель потребляет его один раз через consumePendingPrompt() и обнуляет.
	 */
	pendingPrompt: string | null;
	/**
	 * Одноразовый флаг «открыть чистый новый чат». Ставится, когда панель вызвана
	 * ИЗ контентной области (кнопки «Спросить репетитора» / «Объясни проще» / чипы) —
	 * пользователь начинает новый разбор, а не продолжает прошлый диалог. Ручное
	 * открытие ✦-кнопкой в топбаре (toggle) флаг НЕ ставит → продолжается последняя
	 * сессия (поведение по умолчанию). Панель потребляет через consumeRequestNewChat().
	 */
	requestNewChat: boolean;
	openPanel: (kind: Exclude<SidePanelKind, null>, panelKey?: string | null) => void;
	/** Открыть панель репетитора с предзаполненным вопросом (НЕ авто-отправка). */
	openWithPrompt: (kind: Exclude<SidePanelKind, null>, prompt: string) => void;
	/** Прочитать и обнулить pendingPrompt (вызывает панель при открытии). */
	consumePendingPrompt: () => string | null;
	/** Прочитать и обнулить requestNewChat (вызывает панель при открытии). */
	consumeRequestNewChat: () => boolean;
	/** Прочитать и обнулить panelKey (конкретная сессия при открытии). */
	consumePanelKey: () => string | null;
	closePanel: () => void;
	toggle: (kind: Exclude<SidePanelKind, null>) => void;
}

export const useSidePanelStore = create<SidePanelState>((set, get) => ({
	open: false,
	kind: null,
	panelKey: null,
	pendingPrompt: null,
	requestNewChat: false,
	// Открытие из контентной кнопки. Если panelKey задан (повторное открытие
	// конкретной сессии) — НЕ форсим новый чат; иначе (просто «Спросить
	// репетитора») — чистый новый чат.
	openPanel: (kind, panelKey = null) =>
		set({ open: true, kind, panelKey, pendingPrompt: null, requestNewChat: panelKey === null }),
	openWithPrompt: (kind, prompt) =>
		set({ open: true, kind, panelKey: null, pendingPrompt: prompt, requestNewChat: true }),
	consumePendingPrompt: () => {
		const p = get().pendingPrompt;
		if (p !== null) set({ pendingPrompt: null });
		return p;
	},
	consumeRequestNewChat: () => {
		const r = get().requestNewChat;
		if (r) set({ requestNewChat: false });
		return r;
	},
	consumePanelKey: () => {
		const key = get().panelKey;
		if (key !== null) set({ panelKey: null });
		return key;
	},
	closePanel: () =>
		set({ open: false, kind: null, panelKey: null, pendingPrompt: null, requestNewChat: false }),
	toggle: (kind) => {
		const { open, kind: current } = get();
		if (open && current === kind) {
			set({ open: false, kind: null, panelKey: null, pendingPrompt: null, requestNewChat: false });
		} else {
			// Ручное открытие ✦-кнопкой — продолжаем последнюю сессию (requestNewChat=false).
			set({ open: true, kind, panelKey: null, pendingPrompt: null, requestNewChat: false });
		}
	},
}));
